import React from 'react';
import Icon, { IconProps } from '../primitives/Icon';
import Heading from '../primitives/Heading';
import { cn } from '../../utils/cn';
import Typography from '../primitives/Typography';
import Button from '../primitives/Button';

export type FeatureCardProps = {
  icon?: IconProps['name'];
  title: string;
  description: string;
  customImage?: string;
  className?: string;
  variant?: 'default' | 'image-only';
  animate?: boolean;
  delay?: number;
  avatar?: React.ReactNode;
  author?: string;
  link?: { href: string; label: string; variant?: 'link' | 'secondary'; size?: 'sm' | 'md' | 'lg' };
  bullets?: string[];
};

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
  customImage,
  className = '',
  variant = 'default',
  animate = false,
  delay = 0,
  avatar,
  author,
  link,
  bullets,
}) => {
  const animationStyle = animate ? {
    animationDelay: `${delay}s`,
    animation: 'fadeInUp 0.2s ease-out forwards',
    opacity: 0,
    transform: 'translateY(20px)',
  } : {};

  return (
    <>
      <div 
        className={`rounded-lg p-6 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col justify-between ${className}`}
        style={animationStyle}
      >
        <div className="flex flex-col flex-1 items-start space-y-4">
          {/* Visual Element */}
          {customImage ? (
            variant === 'image-only' ? (
              <div className="w-16 h-16 flex items-center justify-center">
                <img 
                  src={customImage} 
                  alt={title} 
                  className="object-contain w-full h-full" 
                />
              </div>
            ) : (
              <div className="w-24 h-24 flex items-center justify-center">
                <img 
                  src={customImage} 
                  alt={title} 
                  className="object-contain w-full h-full" 
                />
              </div>
            )
          ) : icon ? (
            <div className="w-8 h-8 flex items-center justify-center">
              <Icon 
                name={icon} 
                size={32} 
                className="text-gray-600 dark:text-gray-400" 
                ariaLabel={`${title} icon`} 
              />
            </div>
          ) : null}
          
          {/* Content */}
          <div className="space-y-1">
            <Heading 
              as="h3" 
              size="sm" 
              className="text-gray-900 dark:text-white font-semibold text-sm"
            >
              {title}
            </Heading>
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
              {description}
            </p>
            {bullets && bullets.length > 0 && (
              <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700 dark:text-gray-200 text-sm">
                {bullets.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            )}
          </div>
          {avatar ? (
              <div className="mt-2">{avatar}</div>
            ) : author ? (
              <Typography variant="caption" color="muted" className="mt-1">
                by {author}
              </Typography>
            ) : null}
        </div>
        {link && (
          <div className="mt-4 flex-shrink-0 flex items-end justify-start flex-grow-0">
            <a
              href={link.href}
              className="inline-block"
            >
              <Button variant={link.variant || 'secondary'} size={link.size || 'md'}>{link.label}</Button>
            </a>
          </div>
        )}
      </div>
      {animate && (
        <style>
          {`
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}
        </style>
      )}
    </>
  );
};

export default FeatureCard;

type AvatarProps = {
  name: string;
  image?: string;
  className?: string;
};

export const Avatar: React.FC<AvatarProps> = ({ name, image, className = '' }) => (
  <div className={cn('flex items-center gap-2', className)}>
    {image && (
      <img
        src={image}
        alt={name}
        className="w-8 h-8 rounded-full object-cover border border-gray-200"
      />
    )}
    <span className="text-xs text-gray-700 font-medium truncate max-w-[100px]">{name}</span>
  </div>
); 